import { DataRoomAccessStatus } from './data-room.enums';

/**
 * What confirming a share does to a selected internal stakeholder, per 20.2.4.2 section 4.
 *
 * The three branches are decided by the stakeholder's own company state, not by anything about
 * the data room, which is why this is a pure function both sides can run: the modal uses it to
 * raise the warning before confirmation, the server uses it to pick the initial access status.
 */
export enum DataRoomShareEffect {
  /** Never invited, or has not accepted the company invitation yet. */
  AUTO_INVITE = 'auto-invite',
  /** Active company access already, including Terminated stakeholders whose access is intact. */
  GRANT_DIRECTLY = 'grant-directly',
  /** Previously active, later revoked — company access is restored by the same action. */
  RESTORE_ACCESS = 'restore-access',
}

export type StakeholderShareState = {
  isAccessRevoked: boolean;
  isActive: boolean;
};

export const shareEffectFor = ({
  isAccessRevoked,
  isActive,
}: StakeholderShareState): DataRoomShareEffect => {
  if (isAccessRevoked) return DataRoomShareEffect.RESTORE_ACCESS;

  return isActive ? DataRoomShareEffect.GRANT_DIRECTLY : DataRoomShareEffect.AUTO_INVITE;
};

/**
 * The status the access row starts on.
 *
 * Restoring company access happens inside the same confirmation as the grant, so a revoked
 * stakeholder never passes through Pending — there is nothing left for them to accept. Only
 * someone who still has a company invitation outstanding waits.
 */
export const initialAccessStatusForEffect = (effect: DataRoomShareEffect): DataRoomAccessStatus =>
  effect === DataRoomShareEffect.AUTO_INVITE
    ? DataRoomAccessStatus.PENDING
    : DataRoomAccessStatus.ACTIVE;

/** Both non-direct branches trigger a company-level action, so both are warned about. */
export const shareNeedsWarning = (effect: DataRoomShareEffect): boolean =>
  effect !== DataRoomShareEffect.GRANT_DIRECTLY;

/** External recipients always start Pending — they become Active on clicking their link. */
export const EXTERNAL_INITIAL_ACCESS_STATUS = DataRoomAccessStatus.PENDING;
